import { Button, Stack, Typography, TypographyProps } from '@mui/material';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { User } from 'types/user';
import BasicDialog from 'views/components/modals/BasicDialog';
import { IntakeForm } from 'views/pages/client-record/useIntakeForm';
import InfoItem from './InfoItem';

interface Props extends ComponentProps<typeof BasicDialog> {
  user: User;
  intakeForm: IntakeForm;
}

const IntakeInfoItem = (props: ComponentProps<typeof InfoItem>) => <InfoItem {...props} />;

const IntakeTitle = ({ children, ...props }: TypographyProps) => (
  <Typography {...props} variant="h3" sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
    {children}
  </Typography>
);

export default function IntakeFormSummaryDialog({ user, intakeForm, onClose, ...props }: Props) {
  return (
    <BasicDialog
      {...props}
      actions={
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      }
    >
      <IntakeFormSummary user={user} intakeForm={intakeForm} />
    </BasicDialog>
  );
}

export function IntakeFormSummary({ user, intakeForm }: { user: User; intakeForm: IntakeForm }) {
  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Personal Information" />
        </IntakeTitle>
        <IntakeInfoItem label={<FormattedMessage defaultMessage="First Name:" />} value={user.firstName} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Last Name:" />} value={user.lastName} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Email:" />} value={user.email} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Address:" />} value={user.address} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Postal Code:" />} value={user.postalCode} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Cell Phone Number:" />} value={user.client.phoneNumber} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Current Status:" />} value={user.client.sentimentalStatus} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Age:" />} value={user.client.age} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Gender:" />} value={user.client.gender} />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Emergency Contact:" />
        </IntakeTitle>
        <IntakeInfoItem label={<FormattedMessage defaultMessage="First Name:" />} value={user.client.emergencyContact.firstName} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Last Name:" />} value={user.client.emergencyContact.lastName} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Relationship:" />} value={user.client.emergencyContact.relationship} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Email:" />} value={user.client.emergencyContact.email} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Cell Phone Number:" />} value={user.client.emergencyContact.phoneNumber} />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Why you're here" />
        </IntakeTitle>
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="Please list one or more presenting problems:" />}
          value={intakeForm.otherInfo.otherInfo_1}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="What are your goals for counselling?" />}
          value={intakeForm.otherInfo.otherInfo_2}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="What do you consider to be your strengths?" />}
          value={intakeForm.otherInfo.otherInfo_3}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="What do you like most about yourself?" />}
          value={intakeForm.otherInfo.otherInfo_4}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="What are effective coping strategies that you have learned?" />}
          value={intakeForm.otherInfo.otherInfo_5}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Treatment History" />
        </IntakeTitle>
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="Are you currently receiving psychiatric services, professional counseling or psychotherapy elsewhere?" />
          }
          value={intakeForm.treatmentHistory.treatmentHistory_1}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="Have you had previous psychotherapy?" />}
          value={intakeForm.treatmentHistory.treatmentHistory_2}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Previous Provider's Name" />}
          value={intakeForm.treatmentHistory.treatmentHistory_2_1}
        />
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="Are you currently taking prescribed psychiatric medication (antidepressants or others)?" />
          }
          value={intakeForm.treatmentHistory.treatmentHistory_3}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="Please list:" />}
          value={intakeForm.treatmentHistory.treatmentHistory_3_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Prescribed by:" />}
          value={intakeForm.treatmentHistory.treatmentHistory_3_2}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Mental Health History" />
        </IntakeTitle>
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Have you had suicidal thoughts recently (past month)?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Have you had them in the past?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_2}
        />

        <Stack spacing={1}>
          <Typography variant="h4">Have you ever experienced any of the following?</Typography>
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Extreme depressed mood" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_1}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Dramatic mood swings" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_2}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Rapid speech" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_3}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Extreme anxiety" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_4}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Panic attacks" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_5}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Phobias" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_6}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Sleep disturbances" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_7}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Hallucinations" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_8}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Unexplained losses of time" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_9}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Unexplained memory lapses" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_10}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Alcohol/substance abuse" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_11}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Frequent body complaints" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_12}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Eating disorder" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_13}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Body image problems" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_14}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Repetitive thoughts (e.g. obsessions)" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_15}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Repetitive behaviors (e.g. frequent checking, hand washing)" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_16}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Homicidal thoughts" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_17}
          />
          <IntakeInfoItem
            label={<FormattedMessage defaultMessage="Suicidal attempts" />}
            value={intakeForm.mentalHealthHistory.mentalHealthHistory_3_18}
          />
        </Stack>

        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="Trauma History: Do you have a history of being abused emotionally, sexually, physically or by neglect?" />
          }
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_4}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Please describe when, where and by whom:" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_4_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Do you regularly use alcohol?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_5}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="In a typical month, how often do you have 4 or more drinks in a 24 hour period?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_5_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="How often do you engage recreational drug use?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_6}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Substances Used:" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_6_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Do you smoke cigarettes or use other tobacco products?" />}
          value={intakeForm.mentalHealthHistory.mentalHealthHistory_7}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Social History" />
        </IntakeTitle>
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Were you adopted?" />} value={intakeForm.socialHistory.socialHistory_1} />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Where did you grow up?" />}
          value={intakeForm.socialHistory.socialHistory_2}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="List your siblings and their ages:" />}
          value={intakeForm.socialHistory.socialHistory_3}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="What was your father's occupation?" />}
          value={intakeForm.socialHistory.socialHistory_4}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="What was your mother's occupation?" />}
          value={intakeForm.socialHistory.socialHistory_5}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Did your parents' divorce?" />}
          value={intakeForm.socialHistory.socialHistory_6}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If so, how old were you when they divorced?" />}
          value={intakeForm.socialHistory.socialHistory_6_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If your parents divorced, who did you live with?" />}
          value={intakeForm.socialHistory.socialHistory_6_2}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Describe your father and your relationship with him:" />}
          value={intakeForm.socialHistory.socialHistory_7}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Describe your mother and your relationship with her:" />}
          value={intakeForm.socialHistory.socialHistory_8}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="How old were you when you left home?" />}
          value={intakeForm.socialHistory.socialHistory_9}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Has anyone in your immediate family died?" />}
          value={intakeForm.socialHistory.socialHistory_10}
        />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Who and when?" />} value={intakeForm.socialHistory.socialHistory_11} />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Are you currently in a romantic relationship?" />}
          value={intakeForm.socialHistory.socialHistory_12}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If yes, how long have you been in this relationship?" />}
          value={intakeForm.socialHistory.socialHistory_13}
        />
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="On a scale of 1-10 (10 being the highest quality), how would you rate your current relationship?" />
          }
          value={intakeForm.socialHistory.socialHistory_14}
        />
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="In the last year, have you experienced any significant life changes or stressors? If yes, please explain:" />
          }
          value={intakeForm.socialHistory.socialHistory_15}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Educational History" />
        </IntakeTitle>
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Highest Grade Completed?" />}
          value={intakeForm.educationalHistory.educationalHistory_1}
        />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Where?" />} value={intakeForm.educationalHistory.educationalHistory_2} />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Did you attend college?" />}
          value={intakeForm.educationalHistory.educationalHistory_3}
        />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Where?" />} value={intakeForm.educationalHistory.educationalHistory_4} />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="Major?" />} value={intakeForm.educationalHistory.educationalHistory_5} />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="What is your highest degree attained?" />}
          value={intakeForm.educationalHistory.educationalHistory_6}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Occupational Information" />
        </IntakeTitle>
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Are you currently:" />}
          value={intakeForm.occupationalInfo.occupationalInfo_1}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="How long in present position?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_2}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="What is/was your occupation?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_3}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Where do you work?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_4}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Have you ever served in the military?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_5}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If so, what branch and when?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_6}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Honorable discharge" />}
          value={intakeForm.occupationalInfo.occupationalInfo_7}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Other type discharge" />}
          value={intakeForm.occupationalInfo.occupationalInfo_8}
        />
        <IntakeInfoItem
          // label={<FormattedMessage defaultMessage="If yes, are you happy with your current position?" />}
          label={<FormattedMessage defaultMessage="Are you happy with your current position?" />}
          value={intakeForm.occupationalInfo.occupationalInfo_9}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="Please list any work-related stressors, if any" />}
          value={intakeForm.occupationalInfo.occupationalInfo_10}
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Family Mental Health History" />
        </IntakeTitle>
        <Typography variant="h5">
          Has anyone in your family (either immediate family members or relatives) experienced difficulties with the following? (circle any
          that apply and list family member, e.g. sibling parent, uncle, etc.)
        </Typography>
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Depression" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_1_1}{' '}
              {intakeForm.familyHistory.familyHistory_1_2 && <>({intakeForm.familyHistory.familyHistory_1_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Bipolar disorder" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_2_1}{' '}
              {intakeForm.familyHistory.familyHistory_2_2 && <>({intakeForm.familyHistory.familyHistory_2_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Anxiety disorder" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_3_1}{' '}
              {intakeForm.familyHistory.familyHistory_3_2 && <>({intakeForm.familyHistory.familyHistory_3_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Panic attacks" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_4_1}{' '}
              {intakeForm.familyHistory.familyHistory_4_2 && <>({intakeForm.familyHistory.familyHistory_4_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Schizophrenia" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_5_1}{' '}
              {intakeForm.familyHistory.familyHistory_5_2 && <>({intakeForm.familyHistory.familyHistory_5_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Alcohol/substance abuse" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_6_1}{' '}
              {intakeForm.familyHistory.familyHistory_6_2 && <>({intakeForm.familyHistory.familyHistory_6_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Eating disorders" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_7_1}{' '}
              {intakeForm.familyHistory.familyHistory_7_2 && <>({intakeForm.familyHistory.familyHistory_7_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Learning disabilities" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_8_1}{' '}
              {intakeForm.familyHistory.familyHistory_8_2 && <>({intakeForm.familyHistory.familyHistory_8_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Trauma history" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_9_1}{' '}
              {intakeForm.familyHistory.familyHistory_9_2 && <>({intakeForm.familyHistory.familyHistory_9_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Suicide attempts" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_10_1}{' '}
              {intakeForm.familyHistory.familyHistory_10_2 && <>({intakeForm.familyHistory.familyHistory_10_2})</>}
            </>
          }
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Chronic illness" />}
          value={
            <>
              {intakeForm.familyHistory.familyHistory_11_1}{' '}
              {intakeForm.familyHistory.familyHistory_11_2 && <>({intakeForm.familyHistory.familyHistory_11_2})</>}
            </>
          }
        />
      </Stack>

      <Stack spacing={2}>
        <IntakeTitle>
          <FormattedMessage defaultMessage="Health And Social Information" />
        </IntakeTitle>
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Do you currently have a primary physician?" />}
          value={intakeForm.healthInfo.healthInfo_1}
        />
        <IntakeInfoItem label={<FormattedMessage defaultMessage="If yes, who is it?" />} value={intakeForm.healthInfo.healthInfo_2} />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Are you currently seeing more than one medical health specialist?" />}
          value={intakeForm.healthInfo.healthInfo_3}
        />
        <IntakeInfoItem
          direction="column"
          label={<FormattedMessage defaultMessage="If yes, please list:" />}
          value={intakeForm.healthInfo.healthInfo_4}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="When was your last physical?" />}
          value={intakeForm.healthInfo.healthInfo_5}
        />
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="Please list any persistent physical symptoms or health concerns (e.g. chronic pain, headaches, hypertension, diabetes, etc.:" />
          }
          value={intakeForm.healthInfo.healthInfo_6}
        />
        <IntakeInfoItem
          direction="column"
          label={
            <FormattedMessage defaultMessage="Are you currently on medication to manage a physical health concern? If yes, please list:" />
          }
          value={intakeForm.healthInfo.healthInfo_7}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Are you having any problems with your sleep habits?" />}
          value={intakeForm.healthInfo.healthInfo_8}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If yes, check where applicable:" />}
          value={intakeForm.healthInfo.healthInfo_9}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="How many times per week do you exercise?" />}
          value={intakeForm.healthInfo.healthInfo_10}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Approximately how long each time?" />}
          value={intakeForm.healthInfo.healthInfo_11}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Are you having any difficulty with appetite or eating habits?" />}
          value={intakeForm.healthInfo.healthInfo_12}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="If yes, check where applicable:" />}
          value={intakeForm.healthInfo.healthInfo_13}
        />
        <IntakeInfoItem
          label={<FormattedMessage defaultMessage="Have you experienced significant weight change in the last 2 months?" />}
          value={intakeForm.healthInfo.healthInfo_14}
        />
      </Stack>
    </Stack>
  );
}
